import { AfterViewInit, Component, OnInit, ViewChild, inject, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { GlobalService } from "@frontend/app/serivces/global.service";
import { MasterService } from "@frontend/app/serivces/master.service";
import { ToastrService } from "ngx-toastr";
import { SanitizerPipe } from "../../../pipes/sanitizer.pipe";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { tap, Subscription } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { UserRolePermissionsDialogComponent } from "./user-role-permissions-dialog/user-role-permissions-dialog.component";

interface MenuItem {
  MenuID: any;
  MenuName: string;
  RouterPath: string;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  expanded?: boolean;
  ChildList?: MenuItem[];
  MenuWithPermissions?: any[];
}

interface UserType {
  UserTypeID: number;
  UserType: string;
  IsActive: boolean;
  MasterMenuList?: MenuItem[];
  isNew?: boolean;
  isLoading?: boolean;
}

// MODIFIED: User interface now uses an array for roles
interface User {
  UMID: number;
  UserLoginID: string;
  UserMobile: string;
  UserEmail: string;
  UserTypeIDs: number[]; // Changed from UserTypeID: number
  isUpdating?: boolean;
}

@Component({
  selector: "app-navigation-master",
  standalone: true,
  imports: [CommonModule, FormsModule, SanitizerPipe, SharedModule, UserRolePermissionsDialogComponent],
  templateUrl: "./usertype-master.component.html",
  styleUrl: "./usertype-master.component.scss",
  animations: [
    trigger("indicatorRotate", [
      state("collapsed", style({ transform: "rotate(270deg)" })),
      state("expanded", style({ transform: "rotate(360deg)" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4,0.0,0.2,1)")),
    ]),
  ],
})
export class NavigationMasterComponent implements OnInit, OnDestroy {
  private masterService = inject(MasterService);
  private globalService = inject(GlobalService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);

  ngOnInit() {
    this.loadUserTypes();
  }

  //#region variables
  readonly SVG_ICONS = SVG_ICONS;
  selectedTab: "Permission" | "Users" = "Permission";
  userTypeList: UserType[] = [];
  filteredList: UserType[] = [];
  expandedUserTypeId: number | string | null = null;
  userList: User[] = [];
  filteredUserList: User[] = [];
  isUserDataLoaded = false;
  displayedColumns: string[] = [];
  private paginator!: MatPaginator;
  private sort!: MatSort;
  private paginatorSubscription: Subscription | null = null;
  private sortSubscription: Subscription | null = null;
  private originalUserRoles: { [key: number]: number[] } = {};
  pageSize = 10;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    if (mp === this.paginator) return;
    this.paginator = mp;

    if (this.paginatorSubscription) {
      this.paginatorSubscription.unsubscribe();
    }

    if (this.paginator) {
      this.paginatorSubscription = this.paginator.page
        .pipe(
          tap((event: any) => {
            this.pageSize = event.pageSize;
            // Use event.pageIndex and event.pageSize directly
            this.loadUsers(
              event.pageIndex + 1, // API Page Number
              event.pageSize, // API Page Size (Dynamic)
              this.sort?.active || "UserLoginID",
              this.sort?.direction || "asc"
            );
          })
        )
        .subscribe();
    }
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    if (ms === this.sort) return;
    this.sort = ms;

    if (this.sortSubscription) {
      this.sortSubscription.unsubscribe();
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.sortSubscription = this.sort.sortChange.subscribe(() => {
        // Reset to page 1 when sorting changes
        if (this.paginator) this.paginator.pageIndex = 0;

        this.loadUsers(1, this.pageSize, this.sort.active, this.sort.direction);
      });
    }
  }
  dataSource = new MatTableDataSource<any>([]);
  length = 0;
  //#endregion

  // --- Tab Management ---
  onTabChange(tab: "Permission" | "Users") {
    if (this.selectedTab === tab) return;
    this.selectedTab = tab;
    this.expandedUserTypeId = null;

    if (tab === "Users") {
      // If paginator exists (it might not yet), use its size, otherwise default to 5
      this.loadUsers(1, this.pageSize, "UserLoginID", "asc");
    }
  }

  // --- PERMISSION TAB LOGIC (No changes here) ---
  loadUserTypes() {
    this.masterService.getUserTypeList().subscribe((res: any) => {
      this.userTypeList = res.Table || [];
      this.filteredList = [...this.userTypeList];

      this.displayedColumns = ["userInfo", "mobile", ...this.userTypeList.map(role => "role-" + role.UserTypeID), "action"];
    });
  }

  applyPermissionFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.filteredList = this.userTypeList.filter(user => user.UserType?.toLowerCase().includes(filterValue) && !user.isNew);
  }

  toggleRoleExpansion(userType: UserType) {
    const id = userType.isNew ? "new" : userType.UserTypeID;
    if (this.expandedUserTypeId === id) {
      this.expandedUserTypeId = null;
    } else {
      this.expandedUserTypeId = id;
      if (!userType.MasterMenuList) {
        if (userType.isNew) {
          this.loadMasterMenuForNewRole(userType);
        } else {
          this.loadPermissionsForRole(userType);
        }
      }
    }
  }

  addNewRole() {
    if (this.userTypeList.some(u => u.isNew)) {
      this.toastr.info("Please save the new user type before adding another.", "Info");
      return;
    }
    const newRole: UserType = { UserTypeID: 0, UserType: "", IsActive: true, isNew: true };
    this.userTypeList.unshift(newRole);
    this.filteredList.unshift(newRole);
    this.toggleRoleExpansion(newRole);
  }

  loadPermissionsForRole(userType: UserType) {
    userType.isLoading = true;
    this.masterService.GetUserTypeByID(userType.UserTypeID).subscribe((res: any) => {
      const processedMenu = this.globalService.menuSetter(res.Table || []);
      this.initializeMenuPermissions(processedMenu);
      userType.MasterMenuList = processedMenu;
      userType.isLoading = false;
    });
  }

  loadMasterMenuForNewRole(userType: UserType) {
    debugger;
    userType.isLoading = true;
    this.masterService.getMenuList().subscribe((res: any) => {
      const processedMenu = this.globalService.menuSetter(res.Table || []);
      this.initializeMenuPermissions(processedMenu);
      userType.MasterMenuList = processedMenu;
      userType.isLoading = false;
    });
  }

  private initializeMenuPermissions(menus: MenuItem[]) {
    menus.forEach(menu => {
      const permissions = menu.MenuWithPermissions?.[0];
      menu.canAdd = permissions?.CanAdd ?? false;
      menu.canEdit = permissions?.CanEdit ?? false;
      menu.canDelete = permissions?.CanDelete ?? false;
      if (menu.ChildList?.length) {
        this.initializeMenuPermissions(menu.ChildList);
      }
    });
  }

  toggleMenuItem(item: MenuItem) {
    item.expanded = !item.expanded;
  }

  updateAllPermissions(event: any, type: "canAdd" | "canEdit" | "canDelete", masterList: MenuItem[]) {
    const isChecked = event.target.checked;
    this.setPermissionRecursively(type, isChecked, masterList);
  }

  updatePermissionForFamily(event: any, type: "canAdd" | "canEdit" | "canDelete", item: MenuItem) {
    const isChecked = event.target.checked;
    if (item.ChildList?.length) {
      this.setPermissionRecursively(type, isChecked, item.ChildList);
    }
  }

  private setPermissionRecursively(type: "canAdd" | "canEdit" | "canDelete", value: boolean, list: MenuItem[]) {
    list.forEach(item => {
      item[type] = value;
      if (item.ChildList?.length) {
        this.setPermissionRecursively(type, value, item.ChildList);
      }
    });
  }

  onSubmit(userType: UserType) {
    if (!this.validateForm(userType)) return;
    const formattedMenu = this.globalService.reformatMenu(userType.MasterMenuList || []);
    const requestData = {
      UserTypeID: userType.UserTypeID.toString(),
      UserType: userType.UserType,
      IsActive: userType.IsActive,
      MenuPermissions: formattedMenu.map((menu: any) => ({
        MenuID: menu.MenuID.toString(),
        CanAdd: menu.CanAdd,
        CanEdit: menu.CanEdit,
        CanDelete: menu.CanDelete,
        CanView: true,
      })),
    };
    this.masterService.addMenuPermission(requestData).subscribe({
      next: () => {
        this.toastr.success(userType.isNew ? "User type created!" : "User type updated!");
        this.expandedUserTypeId = null;
        this.loadUserTypes();
      },
      error: err => {
        console.error(err);
        this.toastr.error("Failed to save user type.");
      },
    });
  }

  private validateForm(userType: UserType): boolean {
    if (!userType.UserType?.trim()) {
      this.toastr.error("User Type Name is required.", "Validation Error");
      return false;
    }
    const selectedMenu = this.globalService.reformatMenu(userType.MasterMenuList || []);
    if (!selectedMenu.some(item => item.CanAdd || item.CanEdit || item.CanDelete)) {
      this.toastr.error("At least one permission must be selected.", "Validation Error");
      return false;
    }
    return true;
  }

  loadUsers(pageindex: number, pagesize: number, sortColumn: string, sortDirection: string) {
    const sort = sortColumn ? `ORDER BY ${sortColumn} ${sortDirection}` : "ORDER BY UserLoginID asc";
    let centerIDs = sessionStorage.getItem("selectedCenter") || "";
    try {
      const parsed = JSON.parse(centerIDs);
      if (Array.isArray(parsed)) {
        centerIDs = parsed.join(",");
      }
    } catch (e) {
      // If parsing fails, use the original string
    }

    this.masterService.getUserList(pageindex, pagesize, sort, centerIDs).subscribe(res => {
      this.userList = (res.Table || []).map((user: any) => ({
        ...user,
        UserTypeIDs: user.UserTypeID ? [user.UserTypeID] : [],
        isUpdating: false,
      }));

      // Update Table Data
      this.dataSource.data = this.userList;
      this.length = res.TotalCount;

      // FIX 3: Manually update total length
      this.length = res.TotalCount;

      // If the viewchild is active, make sure it knows the length
      if (this.paginator) {
        this.paginator.length = this.length;
      }

      this.storeOriginalRoles(this.userList);
      this.isUserDataLoaded = true;
    });
  }

  applyUserFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.filteredUserList = this.userList
      .filter(user => user.UserLoginID?.toLowerCase().includes(filterValue) || user.UserEmail?.toLowerCase().includes(filterValue) || user.UserMobile?.toLowerCase().includes(filterValue))
      .filter(user => !user.UserTypeIDs.includes(4));
    this.dataSource.data = this.filteredUserList;
  }

  // Track original roles to detect changes

  // Check if user roles have been modified
  hasRoleChanges(user: User): boolean {
    if (!user.UserTypeIDs) return false;

    const originalRoles = this.originalUserRoles[user.UMID] || [];
    const currentRoles = user.UserTypeIDs || [];

    // Check if arrays have the same length and same elements (order doesn't matter)
    return originalRoles.length !== currentRoles.length || !originalRoles.every(role => currentRoles.includes(role));
  }

  // Store original roles when loading users
  private storeOriginalRoles(users: User[]) {
    users.forEach(user => {
      if (user.UMID) {
        this.originalUserRoles[user.UMID] = [...(user.UserTypeIDs || [])];
      }
    });
  }

  // Updates the roles for a specific user
  updateUserRoles(user: User) {
    if (!user.UserTypeIDs) {
      user.UserTypeIDs = [];
    }

    user.isUpdating = true;

    const payload = {
      UMID: user.UMID,
      UserRoleID: user.UserTypeIDs,
    };

    this.masterService.updateUserAccess(payload).subscribe({
      next: res => {
        this.toastr.success(`Successfully updated roles for ${user.UserLoginID}`);
        user.isUpdating = false;
        // Update the original roles after successful update
        if (user.UMID) {
          this.originalUserRoles[user.UMID] = [...(user.UserTypeIDs || [])];
        }
        // After successful update, open the permissions dialog to show the "rights"
        this.openPermissionsDialog(user);
      },
      error: err => {
        console.error("Error updating user roles:", err);
        this.toastr.error(`Failed to update roles for ${user.UserLoginID}`);
        user.isUpdating = false;
      },
    });
  }

  // New method to open the permissions dialog
  openPermissionsDialog(user: User) {
    this.dialog.open(UserRolePermissionsDialogComponent, {
      width: "700px",
      data: { user: user },
      panelClass: "custom-dialog-container",
    });
  }

  // MODIFIED: This function now only updates the local state of the user's roles
  onRoleChange(user: User, roleId: number, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;

    // Ensure the user's role array exists before modifying it
    if (!user.UserTypeIDs) {
      user.UserTypeIDs = [];
    }

    // Add or remove the roleId from the local array based on checkbox state
    if (isChecked) {
      if (!user.UserTypeIDs.includes(roleId)) {
        user.UserTypeIDs.push(roleId);
      }
    } else {
      const index = user.UserTypeIDs.indexOf(roleId);
      if (index > -1) {
        user.UserTypeIDs.splice(index, 1);
      }
    }
  }

  onCancel() {
    this.expandedUserTypeId = null;
    this.loadUserTypes();
  }

  ngOnDestroy() {
    this.paginatorSubscription?.unsubscribe();
    this.sortSubscription?.unsubscribe();
  }
}
